import FileGeometryLoader from "../../output/assets/geometries/file-geometry-loader.js";
import AsyncLoader from "../../exec-utils/async-loader.js";

class LoadingFile extends AsyncLoader.Element {
    constructor(gltfLoader, path) {
        super();

        this._gltfLoader = gltfLoader;
        this._path = path;

        this._promise = null;
        this._geometries = null;
        this._unload = false;
    }

    load() {
        if (this._promise) {
            throw new Error("Element is already loaded or loading");
        }

        this._promise = new Promise((resolve, reject) => {
            this._gltfLoader.load(this._path, (gltf) => resolve(gltf), undefined, (error) => reject(error));
        }).then(gltf => {
            if (this._unload) {
                gltf.scene.traverse(obj => {
                    if (obj.geometry) {
                        obj.geometry.dispose();
                    }
                });
            } else {
                this._geometries = new Map();

                gltf.scene.traverse(obj => {
                    if (obj.geometry) {
                        restoreGeometryMaterialIndices(obj);
                    }
                });

                gltf.scene.traverse(obj => {
                    if (obj.geometry) {
                        this._geometries.set(obj.name, obj.geometry);
                    }
                });

                return this._geometries;
            }
        });

        return this._promise.catch(error => {
            console.error("Failed to load glb: "+this._path);
        });
    }

    unload() {
        if (!this._promise) {
            throw new Error("Element is not loaded or loading");
        }

        this._unload = true;

        if (this._geometries) {
            for (const [key, geometry] of this._geometries) {
                geometry.dispose();
            }

            this._geometries = null;
        }
    }
}

/**
 * Assigns material indices (as indicated by glTF material names) to geometry groups
 * @param {THREE.Mesh} mesh Mesh whose geometry groups we want to modify according to names of its materials
 */
function restoreGeometryMaterialIndices(mesh) {
    const geometry = mesh.geometry;

    if (mesh.material) {
        if (Array.isArray(mesh.material)) {
            for (let group of geometry.groups) {
                let material = mesh.material[group.materialIndex];
                if (material) {
                    let name = material.name;
                    let index = parseInt(name);
                    if (String(index) === name) {
                        group.materialIndex = index;
                    }
                }
            }
        } else {
            let name = mesh.material.name;
            let index = parseInt(name);
            if (String(index) === name) {
                if (geometry.groups && geometry.groups.length > 0) {
                    for (let group of geometry.groups) {
                        group.materialIndex = index;
                    }
                } else {
                    let indices = geometry.getIndex();
                    let count;
                    if (indices) {
                        count = indices.count;
                    } else {
                        let position = geometry.getAttribute('position');
                        count = position.count;
                    }
                    geometry.addGroup(0, count, index);
                }
            }
        }
    }
}

class ThreeGltfGeometryLoader extends FileGeometryLoader {
    constructor(gltfLoader) {
        super();

        this._gltfLoader = gltfLoader;

        this._asyncLoader = new AsyncLoader(this._makeLoadElement.bind(this));
        this._geometries = new Map();
    }

    async load(key, path) {
        if (this._geometries.has(key)) {
            throw new Error("Geometry is already loaded or loading");
        }

        const geoInfo = { path };
        this._geometries.set(key, geoInfo);

        const gltfGeometries = await this._asyncLoader.load(path);

        if (gltfGeometries) {
            return gltfGeometries.get(key);
        } else {
            return;
        }
    }

    unload(key) {
        if (!this._geometries.has(key)) {
            throw new Error("Geometry is not loaded or loading");
        }

        const geoInfo = this._geometries.get(key);
        this._asyncLoader.unload(geoInfo.path);
        this._geometries.delete(key);
    }

    _makeLoadElement(path) {
        return new LoadingFile(this._gltfLoader, path);
    }
}

export default ThreeGltfGeometryLoader;
